import { Injectable, signal, computed } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject } from 'rxjs';

export interface GifData {
  id: string;
  url: string;
  width: number;
  height: number;
  title: string;
}

export interface ChatMessage {
  id: number;
  user: { id: number; name: string; profile_picture?: string };
  message: string;
  timestamp: string;
  gif?: GifData;
}

export interface ConnectedUser {
  id: number;
  name: string;
  profile_picture?: string;
}

export interface Player {
  id: number;
  first_name: string;
  last_name: string;
  position: string;
  team: string;
  photo?: string;
}

export interface Auction {
  player: Player;
  nominatedBy: { id: number; name: string };
  currentBid: number;
  highBidder: { id: number; name: string };
  startTime: number;
  nominatedByCommissioner?: boolean;
  commissionerName?: string;
}

export type DraftStatus = 'not_started' | 'in_progress' | 'paused' | 'ended';

export interface AuctionResult {
  winner: { id: number; name: string };
  player: Player;
  amount: number;
  timestamp: number;
}

export interface DraftHistoryEntry {
  draft_position: number;
  cost: number;
  player_id: number;
  first_name: string;
  last_name: string;
  position: string;
  pro_team: string;
  photo?: string;
  team_id: number;
  team_name: string;
  user_id: number;
  user_name: string;
}

export interface TeamInfo {
  team_id: number;
  team_name: string;
  user_id: number;
  user_name: string;
  draft_order: number;
}

export interface DraftSettings {
  status: DraftStatus;
  nominationOrder: number[];
  currentNominatorIndex: number;
  currentNominatorUserId: number | null;
  lastAuctionResult: AuctionResult | null;
  draftHistory: DraftHistoryEntry[];
  teams: TeamInfo[];
  teamBudget?: number;
  rosterSize?: number;
}

export interface DraftState {
  connectedUsers: ConnectedUser[];
  chatHistory: ChatMessage[];
  currentAuction: Auction | null;
  draftSettings: DraftSettings;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private currentLeagueId: number | null = null;
  private visibilityHandler: (() => void) | null = null;

  // Signals for reactive state
  isConnected = signal(false);
  connectedUsers = signal<ConnectedUser[]>([]);
  chatMessages = signal<ChatMessage[]>([]);
  currentAuction = signal<Auction | null>(null);
  draftSettings = signal<DraftSettings | null>(null);
  countdownValue = signal<number | null>(null);

  // Computed signal for current nominator name
  currentNominatorName = computed(() => {
    const settings = this.draftSettings();
    if (!settings?.currentNominatorUserId) return '';
    const team = settings.teams?.find(t => t.user_id === settings.currentNominatorUserId);
    return team?.user_name || 'Unknown';
  });

  // Subjects for event streams
  private userJoined$ = new Subject<{ user: ConnectedUser; connectedUsers: ConnectedUser[] }>();
  private userLeft$ = new Subject<{ user: ConnectedUser; connectedUsers: ConnectedUser[] }>();
  private bidUpdate$ = new Subject<{ bidder: { id: number; name: string }; amount: number; player: Player }>();
  private auctionWon$ = new Subject<AuctionResult>();
  private nomination$ = new Subject<Auction>();
  private error$ = new Subject<{ message: string }>();
  private draftStarted$ = new Subject<{ draftSettings: DraftSettings }>();
  private draftPaused$ = new Subject<{ draftSettings: DraftSettings }>();
  private draftResumed$ = new Subject<{ draftSettings: DraftSettings }>();
  private playerDrafted$ = new Subject<{ result: AuctionResult; draftSettings: DraftSettings }>();
  private nominatorChanged$ = new Subject<{ draftSettings: DraftSettings }>();
  private playerUndrafted$ = new Subject<{ player: { id: number; first_name: string; last_name: string }; team: { id: number; name: string }; draftSettings: DraftSettings }>();

  // Public observables
  onUserJoined = this.userJoined$.asObservable();
  onUserLeft = this.userLeft$.asObservable();
  onBidUpdate = this.bidUpdate$.asObservable();
  onAuctionWon = this.auctionWon$.asObservable();
  onNomination = this.nomination$.asObservable();
  onError = this.error$.asObservable();
  onDraftStarted = this.draftStarted$.asObservable();
  onDraftPaused = this.draftPaused$.asObservable();
  onDraftResumed = this.draftResumed$.asObservable();
  onPlayerDrafted = this.playerDrafted$.asObservable();
  onNominatorChanged = this.nominatorChanged$.asObservable();
  onPlayerUndrafted = this.playerUndrafted$.asObservable();

  connect(): void {
    if (this.socket?.connected) return;

    this.socket = io({
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.isConnected.set(true);

      // Re-join draft room on reconnection (e.g., after mobile browser returns from background)
      if (this.currentLeagueId) {
        console.log('Reconnecting to draft room:', this.currentLeagueId);
        this.socket?.emit('join-draft', { leagueId: this.currentLeagueId });
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.isConnected.set(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.isConnected.set(false);
    });

    this.setupEventListeners();
    this.setupVisibilityHandler();
  }

  private setupVisibilityHandler(): void {
    // Clean up any existing handler
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    // Handle page visibility changes (mobile browser returning from background)
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.socket) {
        console.log('Page became visible, checking socket connection');

        // If socket is disconnected, it will auto-reconnect via Socket.IO
        // If socket thinks it's connected but actually isn't, force reconnect
        if (!this.socket.connected) {
          console.log('Socket disconnected, reconnecting...');
          this.socket.connect();
        } else if (this.currentLeagueId) {
          // Socket is connected but we may have missed events while backgrounded
          // Re-join to get fresh state
          console.log('Re-syncing draft room state');
          this.socket.emit('join-draft', { leagueId: this.currentLeagueId });
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('draft-state', (state: DraftState) => {
      this.connectedUsers.set(state.connectedUsers);
      this.chatMessages.set(state.chatHistory);
      this.currentAuction.set(state.currentAuction);
      this.draftSettings.set(state.draftSettings);
    });

    this.socket.on('user-joined', (data: { user: ConnectedUser; connectedUsers: ConnectedUser[] }) => {
      this.connectedUsers.set(data.connectedUsers);
      this.userJoined$.next(data);
    });

    this.socket.on('user-left', (data: { user: ConnectedUser; connectedUsers: ConnectedUser[] }) => {
      this.connectedUsers.set(data.connectedUsers);
      this.userLeft$.next(data);
    });

    this.socket.on('chat-message', (message: ChatMessage) => {
      this.chatMessages.update(messages => [...messages, message]);
    });

    this.socket.on('nomination', (auction: Auction) => {
      this.currentAuction.set(auction);
      this.nomination$.next(auction);
    });

    this.socket.on('bid-update', (data: { bidder: { id: number; name: string }; amount: number; player: Player }) => {
      this.currentAuction.update(auction => {
        if (auction) {
          return { ...auction, currentBid: data.amount, highBidder: data.bidder };
        }
        return auction;
      });
      this.bidUpdate$.next(data);
    });

    this.socket.on('auction-won', (data: AuctionResult) => {
      this.currentAuction.set(null);
      this.auctionWon$.next(data);
    });

    this.socket.on('draft-started', (data: { draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.draftStarted$.next(data);
    });

    this.socket.on('draft-paused', (data: { draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.draftPaused$.next(data);
    });

    this.socket.on('draft-resumed', (data: { draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.draftResumed$.next(data);
    });

    this.socket.on('player-drafted', (data: { result: AuctionResult; draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.playerDrafted$.next(data);
    });

    this.socket.on('nominator-changed', (data: { draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.nominatorChanged$.next(data);
    });

    this.socket.on('player-undrafted', (data: { player: { id: number; first_name: string; last_name: string }; team: { id: number; name: string }; draftSettings: DraftSettings }) => {
      this.draftSettings.set(data.draftSettings);
      this.playerUndrafted$.next(data);
    });

    this.socket.on('countdown-tick', (data: { value: number }) => {
      this.countdownValue.set(data.value);
      if (data.value <= 1) {
        setTimeout(() => this.countdownValue.set(null), 1000);
      }
    });

    this.socket.on('countdown-stopped', () => {
      this.countdownValue.set(null);
    });

    this.socket.on('error', (error: { message: string }) => {
      console.error('Socket error:', error.message);
      this.error$.next(error);
    });
  }

  disconnect(): void {
    if (this.currentLeagueId) {
      this.leaveDraft(this.currentLeagueId);
    }

    // Clean up visibility change handler
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    this.socket?.disconnect();
    this.socket = null;
    this.isConnected.set(false);
    this.connectedUsers.set([]);
    this.chatMessages.set([]);
    this.currentAuction.set(null);
    this.draftSettings.set(null);
    this.countdownValue.set(null);
  }

  joinDraft(leagueId: number): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    this.currentLeagueId = leagueId;
    this.socket.emit('join-draft', { leagueId });
  }

  leaveDraft(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('leave-draft', { leagueId });
    this.currentLeagueId = null;
  }

  sendChatMessage(leagueId: number, message: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('chat-message', { leagueId, message });
  }

  sendGifMessage(leagueId: number, gif: GifData): void {
    if (!this.socket?.connected) return;
    this.socket.emit('chat-message', { leagueId, message: '', gif });
  }

  nominatePlayer(leagueId: number, playerId: number, startingBid: number = 1, onBehalfOfUserId?: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('nominate-player', { leagueId, playerId, startingBid, onBehalfOfUserId });
  }

  placeBid(leagueId: number, amount: number, onBehalfOfUserId?: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('place-bid', { leagueId, amount, onBehalfOfUserId });
  }

  endAuction(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('end-auction', { leagueId });
  }

  startDraft(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('start-draft', { leagueId });
  }

  pauseDraft(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('pause-draft', { leagueId });
  }

  resumeDraft(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('resume-draft', { leagueId });
  }

  advanceDraft(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('advance-draft', { leagueId });
  }

  undraftPlayer(leagueId: number, playerId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('undraft-player', { leagueId, playerId });
  }

  startCountdown(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('start-countdown', { leagueId });
  }

  stopCountdown(leagueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('stop-countdown', { leagueId });
  }
}
